import { CardKind, LevelSim, RaiseBlock, SimConstants, Tower, auraSlowAtTier, dealsAscend, hexBonusOf, isHex, raiseBlock, slowAtTier, troopTierMult } from "@highmarch/domain";

/** One stat an Upgrade card changes: its label and the value now and after, as shown ("DMG 6 → 9"). */
export interface StatChange { readonly label: string; readonly now: string; readonly next: string }

/**
 * Shots a second at `interval` ticks between them: a tower fires on the tick after its cooldown runs out, so a
 * cooldown of N ticks is N + 1 ticks a shot (systems/combat.ts). The tower sheet's DPS uses the same sum.
 */
export function shotsPerSecond(interval: number): number { return interval > 0 ? SimConstants.TicksPerSecond / (interval + 1) : 0; }

/** A range in cells: one decimal, or two where a quarter-cell tier bonus would otherwise round away (2.25, not 2.3). */
export function formatRange(v: number): string {
  const tenths = v * 10;
  return Math.abs(tenths - Math.round(tenths)) < 1e-6 ? v.toFixed(1) : v.toFixed(2);
}

/** A multiplier as its gain over 1 in whole percent: 1.25 → "+25%". */
export function gainPercent(mult: number): string { return `+${Math.round((mult - 1) * 100)}%`; }

/**
 * What the Upgrade card would change on `t`: damage, range and DPS at `t.tier + 1` against now, read from the sim's
 * own tier maths (damageAtTier, previewRange), so the preview is exactly what the upgrade does; a Frost tower's slow
 * and a Hex Totem's curse where those are what it does. Stats a tier leaves alone (a Frost tower's damage, a Beacon's)
 * are left out. Null when the tower is at max tier.
 */
export function upgradeChanges(sim: LevelSim, t: Tower): StatChange[] | null {
  if (!sim.canUpgrade(t)) return null;
  const next = t.tier + 1;
  const interval = sim.fireIntervalOf(t);
  const d0 = sim.damageOf(t), d1 = sim.damageAtTier(t, next);
  const r0 = sim.rangeOf(t).toNumber(), r1 = sim.previewRange(t.archetype, next).toNumber();
  const rows: StatChange[] = [];
  if (interval > 0) rows.push({ label: "DMG", now: d0.toDecimalString(0), next: d1.toDecimalString(0) });
  rows.push({ label: "RANGE", now: formatRange(r0), next: formatRange(r1) });
  if (interval > 0) {
    const rate = shotsPerSecond(interval);
    rows.push({ label: "DPS", now: (d0.toNumber() * rate).toFixed(0), next: (d1.toNumber() * rate).toFixed(0) });
  }
  rows.push(...controlRows(sim, t, next));
  // The fortress's tier also makes its troops tougher (owner 2026-09-30): the card says so where it is used.
  if (sim.balance.towers[t.archetype].isBase) {
    const R = sim.balance.economy.troops;
    rows.push({ label: "TROOPS", now: gainPercent(troopTierMult(R, t.tier).toNumber()), next: gainPercent(troopTierMult(R, next).toNumber()) });
  }
  const changed = rows.filter((r) => r.now !== r.next);
  return changed.length > 0 ? changed : rows.filter((r) => r.label === "RANGE");
}

const wholePct = (f: number): string => `${Math.round(f * 100)}%`;
const slowText = ([f, ticks]: readonly [{ toNumber(): number }, number]): string => `${wholePct(f.toNumber())} ${+(ticks / SimConstants.TicksPerSecond).toFixed(1)}s`;

/** The rows for what a non-damage tower does instead: a Frost hit's slow and a Blizzard's aura, a Hex Totem's curse. */
function controlRows(sim: LevelSim, t: Tower, next: number): StatChange[] {
  const a = sim.balance.towers[t.archetype], rows: StatChange[] = [];
  if (a.slowFraction.isPositive()) {
    rows.push({ label: "SLOW", now: slowText(slowAtTier(sim, t.archetype, t.tier)), next: slowText(slowAtTier(sim, t.archetype, next)) });
    const aura0 = auraSlowAtTier(sim, t.archetype, t.tier), aura1 = auraSlowAtTier(sim, t.archetype, next);
    if (aura0.isPositive() || aura1.isPositive()) rows.push({ label: "AURA SLOW", now: wholePct(aura0.toNumber()), next: wholePct(aura1.toNumber()) });
  }
  if (isHex(a)) rows.push({ label: "CURSE", now: `+${wholePct(hexBonusOf(sim, t).toNumber())}`, next: `+${wholePct(hexBonusOf(sim, t, next).toNumber())}` });
  return rows;
}

/** The Upgrade card's selected tower, for the tray: "Archer · Tier 2 → 3" and its before → after strip. */
export interface UpgradeSelection { readonly title: string; readonly changes: readonly StatChange[] }

export function upgradeSelection(sim: LevelSim, t: Tower): UpgradeSelection | null {
  const changes = upgradeChanges(sim, t);
  if (changes === null) return null;
  return { title: `${sim.balance.towers[t.archetype].name} · Tier ${t.tier} → ${t.tier + 1}`, changes };
}

/**
 * The tower sheet's upgrade button. Upgrades come only from Upgrade cards, and the sheet is only open with no card in
 * hand, so: when the offer on the table shows an Upgrade card the button uses it (`cardIndex`, the card to pick) on
 * this tower; otherwise it is off and says when the next one comes (DESIGN §4.4 paces them, so the wait is known).
 */
export interface SheetUpgrade { readonly cardIndex: number; readonly label: string; readonly sub: string }

export function sheetUpgrade(sim: LevelSim, t: Tower): SheetUpgrade {
  const block = raiseBlock(sim, t);
  if (block === RaiseBlock.NeedsAscend) return { cardIndex: -1, label: "Tier 4: Ascend", sub: ascendWhen(sim) };
  if (block !== RaiseBlock.None) return { cardIndex: -1, label: "Max tier", sub: "At full strength" };
  const label = `Upgrade to Tier ${t.tier + 1}`;
  const offer = sim.currentOffer;
  const card = offer !== null && !offer.hasPick ? offer.cards.findIndex((c) => c.kind === CardKind.Upgrade) : -1;
  if (card >= 0) return { cardIndex: card, label, sub: "Uses the Upgrade card" };
  const n = sim.offersUntilUpgrade;
  return { cardIndex: -1, label, sub: n <= 1 ? "Upgrade card next hand" : `Upgrade card in ${n} hands` };
}

/** When an Ascend card can come: this level deals them, or the level they start at. */
const ascendWhen = (sim: LevelSim): string => (dealsAscend(sim) ? "Needs an Ascend card" : `Ascend cards from level ${sim.balance.branches.pacing.minLevel}`);

/** Why a tap with the Upgrade card on `t` does nothing, in a few words (null: it can take it). */
export function upgradeRefusal(sim: LevelSim, t: Tower): string | null {
  const block = raiseBlock(sim, t), name = sim.balance.towers[t.archetype].name;
  if (block === RaiseBlock.None) return null;
  if (block === RaiseBlock.MaxTier) return `${name} is at max tier`;
  return dealsAscend(sim) ? `${name}: Tier 4 needs an Ascend card` : `${name}: Tier 4 comes from level ${sim.balance.branches.pacing.minLevel}`;
}
